import { URLS } from '@/constants/urls';

import type { YouTubeGlobal, YTNamespace } from '@/types/youtube';

let apiPromise: Promise<YTNamespace> | undefined;
let failPendingLoad: ((error: Error) => void) | undefined;

/**
 * Charge une seule fois le script officiel de l'IFrame Player API YouTube et résout le namespace `YT`.
 *
 * Ce n'est pas une requête métier : aucun appel à nos API, seulement l'injection du script YouTube
 * dont le lecteur a besoin pour détecter la fin et les erreurs d'une vidéo.
 *
 * Chargé à la main plutôt que via `react-youtube` ou `react-player` : l'iframe reste un élément React
 * (src `youtube-nocookie`, `rel=0`, focus, remontage pour « Revoir »), l'API ne fait que s'y attacher,
 * et le besoin se limite à un événement de fin, sans dépendance supplémentaire.
 */
const loadYouTubeIframeApi = async (): Promise<YTNamespace> => {
  const youTubeGlobal = globalThis as YouTubeGlobal;
  if (youTubeGlobal.YT?.Player) return youTubeGlobal.YT;

  apiPromise ??= new Promise<YTNamespace>((resolve, reject) => {
    const previousReady = youTubeGlobal.onYouTubeIframeAPIReady;
    const script = document.createElement('script');

    const fail = (error: Error) => {
      script.remove();
      youTubeGlobal.onYouTubeIframeAPIReady = previousReady;
      apiPromise = undefined;
      failPendingLoad = undefined;
      reject(error);
    };
    failPendingLoad = fail;

    youTubeGlobal.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      if (!youTubeGlobal.YT) {
        fail(new Error('YouTube IFrame API ready without YT namespace'));
        return;
      }
      failPendingLoad = undefined;
      resolve(youTubeGlobal.YT);
    };

    script.src = URLS.YOUTUBE_IFRAME_API_URL;
    script.async = true;
    script.onerror = () => fail(new Error('YouTube IFrame API failed to load'));
    document.head.append(script);
  });

  return apiPromise;
};

// Abandonne un chargement bloqué : le prochain appel réinjecte le script au lieu de rendre la même promesse en attente.
// Exporté à côté du default car il agit sur l'état singleton du module (promesse en cours), inaccessible depuis un autre fichier.
const resetYouTubeIframeApi = () => failPendingLoad?.(new Error('YouTube IFrame API timed out'));

export { resetYouTubeIframeApi };
export default loadYouTubeIframeApi;
